'use client';

import React, { useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { LOCATION } from 'src/hooks/filters/constants';
import { useStrategyForm } from 'src/hooks/strategy/useStrategyForm';
import { useTemplateHydration } from 'src/hooks/strategy/useTemplateHydration';
import { useAiGenerator } from 'src/hooks/aiGenerator/useAiGenerator';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  selectEngageMaxOpportunities,
  updateEngageMaxOpportunities,
} from 'src/redux/slices/strategyFormSlice';
import { getStrategyConfigComponent } from 'src/components/Strategy/config';

const EngageStrategies: React.FC = () => {
  const { selectedPlay, patientJourneyData, updatePatientJourneyData } =
    useStrategyForm();
  const { hydrateTemplate } = useTemplateHydration(LOCATION.STRATEGY_ENGAGE);
  const { dynamicCriteria } = useAiGenerator(LOCATION.STRATEGY_ENGAGE);
  const dispatch = useAppDispatch();
  const engageMaxOpportunities = useAppSelector(selectEngageMaxOpportunities);

  // Local state for patient journey direction
  const [patientJourneyDirection, setPatientJourneyDirection] = React.useState<
    'BEFORE' | 'AFTER'
  >('AFTER');

  const engageTemplate = selectedPlay?.engage_template;
  const templateType = engageTemplate?.type;
  const config = engageTemplate?.config;

  // Sync direction from Redux when patientJourneyData changes (e.g., on edit/copy)
  // Normalizes numeric API values (1=BEFORE, 2=AFTER) to string equivalents
  React.useEffect(() => {
    const raw = patientJourneyData?.target_event_direction as any;
    if (raw === 1 || raw === 'BEFORE') {
      setPatientJourneyDirection('BEFORE');
    } else if (raw === 2 || raw === 'AFTER') {
      setPatientJourneyDirection('AFTER');
    }
  }, [patientJourneyData?.target_event_direction]);

  // Update Redux when direction changes
  const handleDirectionChange = (direction: 'BEFORE' | 'AFTER') => {
    setPatientJourneyDirection(direction);
    updatePatientJourneyData({ target_event_direction: direction });
  };

  // Hydrate form data when selectedPlay changes
  // Only hydrate if there's no existing criteria (criteria is cleared when user selects a new play)
  useEffect(() => {
    if (!config || !templateType || !selectedPlay) return;

    // Only hydrate if we don't have any criteria yet
    // When user selects a new play, criteria is cleared in ChoosePlayStep
    const hasExistingCriteria =
      dynamicCriteria?.criteria_template ||
      (dynamicCriteria?.criteria_inputs &&
        Object.keys(dynamicCriteria.criteria_inputs).length > 0);

    if (!hasExistingCriteria) {
      hydrateTemplate(config, templateType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, templateType, selectedPlay]);

  const handleEngageMaxOpportunitiesChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value === '') {
      dispatch(updateEngageMaxOpportunities('' as any));
    } else if (/^\d+$/.test(value)) {
      const numValue = parseInt(value);
      if (numValue >= 1 && numValue <= 1000) {
        dispatch(updateEngageMaxOpportunities(numValue));
      }
    }
  };

  const handleEngageMaxOpportunitiesBlur = (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    let value = parseInt(e.target.value) || 100;
    if (value < 1) value = 1;
    if (value > 1000) value = 1000;
    dispatch(updateEngageMaxOpportunities(value));
  };

  // Get the configuration component from registry
  const ConfigComponent = getStrategyConfigComponent(templateType);

  // Render strategy configuration component from registry
  const renderStrategyTypeContent = () => {
    if (!ConfigComponent) {
      return null;
    }

    // PatientJourneyConfiguration needs direction props (and uses its own LOCATION values)
    if (templateType === 'PATIENT_JOURNEY') {
      return (
        <ConfigComponent
          direction={patientJourneyDirection}
          onDirectionChange={handleDirectionChange}
        />
      );
    }

    return <ConfigComponent location={LOCATION.STRATEGY_ENGAGE} />;
  };

  if (!selectedPlay) {
    return (
      <div className="flex flex-col gap-4" data-testid="engage-strategies">
        <p className="text-sm text-tertiary">No play selected</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="engage-strategies">
      {/* Header */}
      <div className="flex flex-col gap-1 border-b border-neutral-200 pb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-primary">
          Engage
        </h2>
        {engageTemplate && (
          <p className="text-sm text-secondary">{engageTemplate.name}</p>
        )}
      </div>

      {/* Info Callout */}
      {engageTemplate?.description && (
        <div
          className="flex items-start gap-3 rounded-lg border-l-4 border-sky-500 bg-sky-50 p-4"
          role="note"
          aria-label="Engage information"
          data-testid="engage-info-callout"
        >
          <Icon
            icon={faCircleInfo}
            className="mt-0.5 shrink-0 text-sky-600"
            aria-hidden="true"
          />
          <p className="text-sm text-sky-800">{engageTemplate.description}</p>
        </div>
      )}

      {/* Strategy Configuration from Registry */}
      {renderStrategyTypeContent()}

      {/* Engage Strategy Settings */}
      <div
        className="flex items-start gap-6 rounded-xl border border-neutral-200 bg-neutral-50 p-4"
        data-testid="engage-strategy-settings"
        aria-label="Engage strategy settings"
      >
        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-primary">
            Max Opportunities
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={engageMaxOpportunities}
              onChange={handleEngageMaxOpportunitiesChange}
              onBlur={handleEngageMaxOpportunitiesBlur}
              className="w-20 rounded-full border border-neutral-200 bg-white px-3 py-2 text-center font-medium focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="engage-max-opportunities-input"
              aria-label="Engage max opportunities"
            />
            <span className="text-xs text-neutral-500">Range: 1-1000</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngageStrategies;
